import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { z } from "zod";

export const notificationHistorySchema = z.object({
    id: z.string(),
    noteId: z.string().nullable(),
    priority: z.enum(["high", "medium", "low"]),
    title: z.string(),
    caseNumber: z.string(),
    caseId: z.string(),
    clientName: z.string(),
    status: z.enum(["read", "unread"]),
    recipients: z.array(z.string()),
    sentBy: z.string(),
    sentAt: z.string(),
    readAt: z.string().nullable(),
});

export type NotificationHistory = z.infer<typeof notificationHistorySchema>;

const notificationHistoryResponseSchema = z.object({
    data: z.array(notificationHistorySchema),
    total: z.number(),
});

export const useFetchNotificationHistory = (): UseQueryResult<NotificationHistory[]> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["notification-history"],
        queryFn: async () => {
            const response = await fetch(apiUrl("/notification/history"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch notification history");
            }

            const json = await response.json();
            const parsed = notificationHistoryResponseSchema.parse(json);
            return parsed.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};
